import { useEffect, useState } from 'react';
import { Dialog, DialogContent, Box, Typography, TextField, useTheme } from '@mui/material';
import { FiAlertTriangle } from 'react-icons/fi';
import Button from 'components/Common/Button';

interface Props {
  open: boolean;
  businessName: string;
  onClose: () => void;
  onConfirm: () => void;
}

export default function LostOnboardingConfirmModal({ open, businessName, onClose, onConfirm }: Props) {
  const theme = useTheme();
  const [value, setValue] = useState('');
  const isMatch = value.trim() === businessName.trim() && businessName.trim().length > 0;

  useEffect(() => {
    if (!open) setValue('');
  }, [open]);

  const handleConfirm = () => {
    if (!isMatch) return;
    onConfirm();
    setValue('');
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth='sm' PaperProps={{ sx: { borderRadius: '8px' } }}>
      <DialogContent sx={{ p: { xs: 3, sm: 3 } }}>
        <Box display='flex' justifyContent='center'>
          <Box
            sx={{
              height: 72,
              width: 72,
              borderRadius: '50%',
              backgroundColor: theme.palette.warning.light,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <FiAlertTriangle size={32} color={theme.palette.warning.main} />
          </Box>
        </Box>

        <Typography textAlign='center' fontSize={22} fontWeight={500} mt={2}>
          Change Business Status to Lost Onboarding?
        </Typography>

        <Typography textAlign='center' fontSize={14} color='#6B6B6B' lineHeight={1.6} mt={1}>
          Are you sure you want to change business &ldquo;{businessName}&rdquo; onboarding status to &ldquo;Lost
          Onboarding&rdquo;?
        </Typography>

        <Box
          sx={{
            mt: 2.5,
            p: 2,
            borderRadius: '8px',
            backgroundColor: '#F5F5F7'
          }}
        >
          <Typography fontSize={14} fontWeight={500} color='#1F2937'>
            What will happen:
          </Typography>
          <Box component='ul' sx={{ pl: 2.5, m: 0, mt: 0.5 }}>
            {[
              'Status changes to Lost Onboarding immediately',
              `${businessName} will get deleted from Businesses immediately`,
              'The record stays in Onboarding for future reference'
            ].map((item) => (
              <Typography key={item} component='li' fontSize={14} color='#1F2937' lineHeight={1.6}>
                {item}
              </Typography>
            ))}
          </Box>
        </Box>

        <Typography fontSize={14} color='#777777' mt={2.5}>
          Type &ldquo;<b>{businessName}</b>&rdquo; to confirm
        </Typography>

        <TextField
          fullWidth
          label='Business name'
          placeholder='Enter Business name'
          value={value}
          onChange={(e) => setValue(e.target.value)}
          sx={{
            mt: 2,
            '& .MuiOutlinedInput-root': {
              '& fieldset': { borderColor: '#ECECEC' }
            }
          }}
          InputLabelProps={{ shrink: true }}
        />

        <Box display='flex' justifyContent='flex-end' gap={1.5} mt={3}>
          <Button
            variant='outlined'
            onClick={onClose}
            sx={{
              borderRadius: '24px',
              textTransform: 'none',
              px: 3,
              py: 0.75,
              fontSize: 14,
              borderColor: '#ECECEC',
              color: '#374151',
              '&:hover': {
                borderColor: '#D1D5DB',
                backgroundColor: 'transparent',
                color: '#374151'
              }
            }}
          >
            Cancel
          </Button>

          <Button
            variant='contained'
            disabled={!isMatch}
            onClick={handleConfirm}
            sx={{
              borderRadius: '24px',
              textTransform: 'none',
              px: 3,
              py: 0.75,
              fontSize: 14,
              backgroundColor: theme.palette.warning.main,
              opacity: isMatch ? 1 : 0.5,
              color: '#fff',
              boxShadow: 'none',
              '&:hover': { backgroundColor: theme.palette.warning.main, boxShadow: 'none' },
              '&.Mui-disabled': {
                backgroundColor: theme.palette.warning.main,
                color: '#fff',
                opacity: 0.45
              }
            }}
          >
            Confirm
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
